import React, { useEffect, useState } from "react";
import {
  Card,
  Table,
  Button,
  Input,
  Modal,
  Form,
  message,
  ConfigProvider,
  Popconfirm,
  Space,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  QuestionCircleOutlined,
} from "@ant-design/icons";
import "./faqs.css";
import { routes } from "../../api/routes";
import { callApi } from "../../api/apiCaller";
import Loader from "../../components/loader/loader";
import NotificationBell from "../../components/notificationBell/notificationBell";
import dayjs from "dayjs";

const { TextArea } = Input;

const parseErrorMessage = (error, fallback) =>
  error?.message ||
  error?.response?.data?.message ||
  error?.data?.message ||
  fallback;

const FAQs = () => {
  const [faqs, setFaqs] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingFAQ, setEditingFAQ] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [form] = Form.useForm();

  useEffect(() => {
    getFAQs();
  }, []);

  const getFAQs = () => {
    const onSuccess = (res) => {
      if (res.status === 200) {
        const raw = res.data?.data?.data ?? res.data?.data ?? res.data ?? [];
        const faqsData = Array.isArray(raw) ? raw : raw?.faqs ?? raw?.items ?? [];
        if (!Array.isArray(faqsData)) {
          message.error("Invalid FAQ list response from server");
          setFaqs([]);
          return;
        }
        setFaqs(faqsData);
      }
    };

    callApi(
      "GET",
      routes.getAllFAQs,
      null,
      setIsLoading,
      onSuccess,
      (error) => {
        message.error(parseErrorMessage(error, "Failed to load FAQs"));
      }
    );
  };

  const handleCreate = () => {
    setEditingFAQ(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  const handleEdit = (faq) => {
    setEditingFAQ(faq);
    form.setFieldsValue({
      question: faq.question,
      answer: faq.answer,
    });
    setIsModalVisible(true);
  };

  const handleDelete = (faqId) => {
    let getRes = (res) => {
      if (res.status === 200 || res.status === 201) {
        message.success("FAQ deleted successfully");
        getFAQs();
      } else {
        message.warning(`Delete completed with status ${res.status}`);
        getFAQs();
      }
    };

    callApi(
      "DELETE",
      `${routes.deleteFAQ}/${faqId}`,
      null,
      setIsLoading,
      getRes,
      (error) => {
        message.error(parseErrorMessage(error, "Failed to delete FAQ"));
      }
    );
  };

  const handleSubmit = (values) => {
    // Close modal first, then proceed with API call
    setIsModalVisible(false);
    form.resetFields();
    const currentEditingFAQ = editingFAQ;
    setEditingFAQ(null);
    
    if (currentEditingFAQ) {
      // Update
      let getRes = (res) => {
        if (res.status === 200 || res.status === 201) {
          message.success("FAQ updated successfully");
          getFAQs();
        } else {
          message.warning(`Update completed with status ${res.status}`);
          getFAQs();
        }
      };

      callApi(
        "PATCH",
        `${routes.updateFAQ}/${currentEditingFAQ._id}`,
        values,
        setIsLoading,
        getRes,
        (error) => {
          message.error(parseErrorMessage(error, "Failed to update FAQ"));
        }
      );
    } else {
      // Create
      let getRes = (res) => {
        if (res.status === 200 || res.status === 201) {
          message.success("FAQ created successfully");
          getFAQs();
        } else {
          message.warning(`Create completed with status ${res.status}`);
          getFAQs();
        }
      };

      callApi(
        "POST",
        routes.createFAQ,
        values,
        setIsLoading,
        getRes,
        (error) => {
          message.error(parseErrorMessage(error, "Failed to create FAQ"));
        }
      );
    }
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    form.resetFields();
    setEditingFAQ(null);
  };

  const handleSearch = (value) => {
    setSearchText(value);
  };

  const filteredFAQs = faqs.filter(
    (faq) =>
      faq.question?.toLowerCase().includes(searchText.toLowerCase()) ||
      faq.answer?.toLowerCase().includes(searchText.toLowerCase())
  );


  const columns = [
    {
      title: "No",
      key: "index",
      render: (text, record, index) => index + 1,
      width: 60,
    },
    {
      title: "Question",
      dataIndex: "question",
      key: "question",
      render: (text) => (
        <span style={{ fontWeight: 500, color: "#333" }}>
          {text || "N/A"}
        </span>
      ),
      ellipsis: true,
    },
    {
      title: "Answer",
      dataIndex: "answer",
      key: "answer",
      render: (text) => (
        <span style={{ color: "#666" }}>
          {text && text.length > 100 ? `${text.substring(0, 100)}...` : text || "N/A"}
        </span>
      ),
      ellipsis: true,
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date) => (date ? dayjs(date).format("DD MMM YYYY") : "N/A"),
      width: 120,
    },
    {
      title: "Action",
      key: "action",
      width: 150,
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            icon={<EditOutlined />}
            size="small"
            onClick={() => handleEdit(record)}
          >
            Edit
          </Button>
          <Popconfirm
            title="Delete FAQ"
            description="Are you sure you want to delete this FAQ?"
            onConfirm={() => handleDelete(record._id)}
            okText="Yes"
            cancelText="No"
            okButtonProps={{ danger: true }}
          >
            <Button
              danger
              icon={<DeleteOutlined />}
              size="small"
            >
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: "#FF66B2",
        },
      }}
    >
      <div className="faqs-container">
        <Loader loading={isLoading} />
        <div className="faqs-header">
          <h1 className="faqs-title">FAQ Management</h1>
          <NotificationBell />
        </div>

        <div className="faqs-content">
          <Card className="faqs-card">
            <div className="faqs-actions">
              <Input
                placeholder="Search FAQs by question or answer..."
                prefix={<SearchOutlined />}
                allowClear
                onChange={(e) => handleSearch(e.target.value)}
                style={{ maxWidth: 300 }}
              />
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleCreate}
                size="middle"
              >
                Add FAQ
              </Button>
            </div>

            <Table
              columns={columns}
              dataSource={filteredFAQs}
              rowKey="_id"
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showTotal: (total) => `Total ${total} FAQs`,
                pageSizeOptions: ["10", "20", "50", "100"],
              }}
              className="faqs-table"
            />
          </Card>
        </div>

        <Modal
          title={editingFAQ ? "Edit FAQ" : "Add New FAQ"}
          open={isModalVisible}
          onCancel={handleCancel}
          footer={null}
          width={600}
        >
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            className="faq-form"
          >
            <Form.Item
              label="Question"
              name="question"
              rules={[
                { required: true, message: "Please enter a question" },
                { min: 5, message: "Question must be at least 5 characters" },
                { max: 200, message: "Question must be less than 200 characters" },
              ]}
            >
              <Input
                placeholder="Enter the question"
                size="large"
                prefix={<QuestionCircleOutlined />}
              />
            </Form.Item>

            <Form.Item
              label="Answer"
              name="answer"
              rules={[
                { required: true, message: "Please enter an answer" },
                { min: 10, message: "Answer must be at least 10 characters" },
                { max: 1000, message: "Answer must be less than 1000 characters" },
              ]}
            >
              <TextArea
                placeholder="Enter the answer"
                rows={6}
                showCount
                maxLength={1000}
              />
            </Form.Item>

            <Form.Item>
              <Space>
                <Button type="primary" htmlType="submit" size="large">
                  {editingFAQ ? "Update" : "Create"}
                </Button>
                <Button onClick={handleCancel} size="large">
                  Cancel
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>
      </div>
    </ConfigProvider>
  );
};

export default FAQs;
